import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegionalFormDefaults } from '../../context/RegionalSettingsContext';
import {
    combinePhone,
    getCountryCodeForIso,
    getPhoneCountryCodeOptions,
    phonesEquivalent,
    splitStoredPhone,
} from '../../utils/phone';

/**
 * Compact phone input: country code picker + local number on one row.
 * Default country code comes from regional_settings (onboarding / Regional Settings).
 */
const PhoneNumberFields = ({
    value = '',
    onChange,
    required = false,
    disabled = false,
    className = '',
    localPlaceholder,
}) => {
    const { t } = useTranslation();
    const regional = useRegionalFormDefaults();
    const defaultIso = regional.countryCode;
    const placeholder = localPlaceholder ?? regional.phoneLocalPlaceholder;

    const countryOptions = useMemo(() => getPhoneCountryCodeOptions(), [defaultIso]);
    const initial = splitStoredPhone(value, defaultIso);
    const [iso, setIso] = useState(initial.iso);
    const [local, setLocal] = useState(initial.local);
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const rootRef = useRef(null);
    const lastEmittedRef = useRef(value ?? '');
    /** User explicitly chose a dial country — do not overwrite when regional ISO loads late. */
    const userPickedIsoRef = useRef(false);

    useEffect(() => {
        const nextValue = value ?? '';
        const empty =
            !(String(nextValue).trim()) && !(String(lastEmittedRef.current || '').trim());

        if (phonesEquivalent(nextValue, lastEmittedRef.current, defaultIso)) {
            // Empty field: adopt academy country when regional settings arrive after first paint
            // (otherwise we stay stuck on FALLBACK_ISO=IN → +91 for DE/FR/NL academies).
            if (empty && !userPickedIsoRef.current && defaultIso) {
                setIso((prev) => (prev === defaultIso ? prev : defaultIso));
            }
            return;
        }

        const parts = splitStoredPhone(nextValue, defaultIso);
        setIso(parts.iso);
        setLocal(parts.local);
        lastEmittedRef.current = nextValue;
        // Incoming stored value owns the country; treat as not a manual pick reset.
        if (String(nextValue).trim()) {
            userPickedIsoRef.current = false;
        }
    }, [value, defaultIso]);

    useEffect(() => {
        if (!open) return undefined;

        const onPointerDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                setOpen(false);
                setFilter('');
            }
        };

        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    const selected = countryOptions.find((opt) => opt.iso === iso);
    const fallbackDialCode = regional.phoneDialCode || getCountryCodeForIso(defaultIso);

    const filteredOptions = useMemo(() => {
        const term = filter.trim().toLowerCase();
        if (!term) return countryOptions;
        return countryOptions.filter(
            (opt) =>
                opt.label.toLowerCase().includes(term) ||
                opt.countryCode.includes(term) ||
                opt.iso.toLowerCase().includes(term)
        );
    }, [countryOptions, filter]);

    const emitChange = (nextIso, nextLocal) => {
        const combined = combinePhone(nextIso, nextLocal);
        lastEmittedRef.current = combined;
        onChange?.(combined);
    };

    const handleIsoSelect = (nextIso) => {
        userPickedIsoRef.current = true;
        setIso(nextIso);
        emitChange(nextIso, local);
        setOpen(false);
        setFilter('');
    };

    const handleLocalChange = (nextLocal) => {
        const sanitized = nextLocal.replace(/[^\d\s-()]/g, '');
        setLocal(sanitized);
        emitChange(iso, sanitized);
    };

    return (
        <div ref={rootRef} className={`flex gap-2 ${className}`}>
            <div className="relative shrink-0 w-[7.5rem]">
                <button
                    type="button"
                    onClick={() => !disabled && setOpen((v) => !v)}
                    disabled={disabled}
                    className="w-full min-h-[42px] select text-left flex items-center justify-between gap-1 disabled:opacity-50"
                    aria-label={t('forms.phone.countryCode')}
                    aria-expanded={open}
                    aria-haspopup="listbox"
                >
                    <span className="truncate font-medium">{selected?.countryCode || fallbackDialCode}</span>
                    <svg className="w-4 h-4 shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {open && (
                    <div className="absolute z-30 top-full left-0 mt-1 w-72 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                        <div className="p-2 border-b border-gray-100">
                            <input
                                type="text"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                placeholder={t('forms.phone.searchCountry')}
                                autoFocus
                                className="input text-sm"
                                aria-label={t('forms.phone.searchCountry')}
                            />
                        </div>
                        <ul
                            role="listbox"
                            className="max-h-52 overflow-y-auto py-1"
                        >
                            {filteredOptions.length === 0 ? (
                                <li className="px-3 py-2 text-sm text-gray-500">{t('forms.phone.noMatches')}</li>
                            ) : (
                                filteredOptions.map((opt) => (
                                    <li key={opt.iso}>
                                        <button
                                            type="button"
                                            role="option"
                                            aria-selected={opt.iso === iso}
                                            onClick={() => handleIsoSelect(opt.iso)}
                                            className={`w-full text-left px-3 py-2 text-sm hover:bg-purple-50 ${
                                                opt.iso === iso ? 'bg-purple-50 text-purple-800 font-medium' : 'text-gray-800'
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    </li>
                                ))
                            )}
                        </ul>
                    </div>
                )}
            </div>

            <input
                type="tel"
                value={local}
                onChange={(e) => handleLocalChange(e.target.value)}
                placeholder={placeholder}
                required={required}
                disabled={disabled}
                className="input flex-1 min-w-0"
                aria-label={t('forms.phone.phoneNumber')}
                title={selected ? `${selected.name} (${selected.countryCode})` : regional.phoneFormatHint || undefined}
            />
        </div>
    );
};

export default PhoneNumberFields;
